// core
import { Injectable, Inject } from '@angular/core';
import {
  FormControl, FormGroup, Validators, ValidatorFn
} from '@angular/forms';
import { Observable, Subject } from 'rxjs';

// raptor services
import { RaptorPredicateService } from './raptor-predicate.service';
import { RaptorDataService } from './raptor-data.service';
// raptor pipes
import { RaptorCurrencyPipe } from '../pipes/raptor-currency.pipe';
import { RaptorDecimalPipe } from '../pipes/raptor-decimal.pipe';
import { RaptorPhonePipe } from '../pipes/raptor-phone.pipe';
// raptor constants
import { RAPTOR_COUNTRY_CODES } from '../constant/raptor-constants';

@Injectable()
export class RaptorControlService {
  groupLayouts: Array<string> = ['date_input_3_boxes', 'date_select_month_year', 'bupa_mem_num', 'range'];

  addNewControl$: Observable<any>;
  private addNewControlSubject: Subject<any>;

  constructor(
    private predicateService: RaptorPredicateService,
    private raptorDataService: RaptorDataService,
    private raptorCurrencyPipe: RaptorCurrencyPipe,
    private raptorDecimalPipe: RaptorDecimalPipe,
    private raptorPhonePipe: RaptorPhonePipe,
    @Inject('environment') public environment: any
  ) {
    this.addNewControlSubject = new Subject<string>();
    this.addNewControl$ = this.addNewControlSubject.asObservable();
  }

  addFormControl(question: any) {
    const newGroup: any = {};
    this.createControl(question, newGroup);
    this.addNewControlSubject.next(newGroup);
  }

  /**
   *
   * @param formData
   * @returns Reactive form object
   * @description Convert all the data from the servier into a reactive forms object
   */
  toFormGroup(formData: any) {
    const group: any = {};

    this.raptorDataService.mapAllQuestions(formData)
      .forEach((question: any) => {
        this.createControl(question, group);
      });

    return new FormGroup(group);
  }

  /**
   *
   * @param question
   * @param group
   * @description Create a form control for a question
   */
  createControl(question: any, group: any) {
    if (question.type === 'multiple_choice' || question.type === 'extras') {
      this.setMultipleChoiceControls(question, group);
    } else if (question.type === 'repeater' && question.layout === 'multiple') {
      this.setRepeaterMultipleControls(question, group);
    } else if (question.type === 'autocomplete') {
      this.setAutoCompleteControls(question, group);
    } else if (question.type === 'payment') {
      this.setPaymentControls(question, group);
    } else if (question.type === 'autocomplete_multiple') {
      this.setAutoCompleteMultipleControls(question, group);
    } else if (question.type === 'mobile') {
      this.setMobileControls(question, group);
    } else if (question.layout && this.groupLayouts.indexOf(question.layout) > -1) {
      this.setLayoutControls(question, group);
    } else {
      this.setQuestionControl(question, group);
    }
  }

  /**
   *
   * @param question
   * @param group
   * @description Set controls for multiple choice question
   */
  setMultipleChoiceControls(question: any, group: any) {
    group[question.key] = new FormGroup({});
    question.settings.choices.forEach((option: any) => {
      let answer;
      // this kind sucks because extras values have to be mapped from the is_included flag on the covers object
      if (question.type === 'extras') {
        answer = !!option.is_included;
      } else if (question.data) {
        if (Array.isArray(question.data)) {
          answer = question.data.indexOf(option.value) > -1;
        } else if (typeof question.data === 'object') {
          answer = question.data[option.value];
        }
      }
      group[question.key].addControl(option.value, new FormControl({ value: answer || false, disabled: option.disabled || (question.settings && question.settings.disabled === true) }));
    });
  }

  setRepeaterMultipleControls(question: any, group: any) {
    const uniqueControls: Array<any> = [];

    group[question.key] = new FormGroup({});

    // added support for multiple fields with the same key in the repeaters
    // only make 1 control per key duplication
    if (question.fields && question.fields.length) {
      question.fields.forEach((fieldGroup: any) => {
        fieldGroup.forEach((column: any) => {
          column.forEach((field: any) => {
            if (!uniqueControls.find(({ key }) => key === field.key) && field.key) {
              uniqueControls.push(field);
            }
          });
        });
      });

      uniqueControls.forEach((field: any) => {
        this.createControl(field, group);
      });
    }
  }

  /**
   *
   * @param question
   * @param group
   * @description Set controls for autocomplete question
   */
  setAutoCompleteControls(question: any, group: any) {
    let autoCompleteValue;
    if (question.data !== null && question.data !== undefined) {
      autoCompleteValue = {
        value: question.data,
        label: question.human || question.data,
        disabled: (question.settings && question.settings.disabled === true)
      };
    }
    group[question.key] = new FormControl(autoCompleteValue || '');
  }

  setAutoCompleteMultipleControls(question: any, group: any) {
    let autoCompleteValue!: Array<any>;

    if (question.data !== null && question.data !== undefined) {
      if (question.data && question.data.length) {
        autoCompleteValue = [];
        question.data.forEach((value: string, index: number) => {
          autoCompleteValue.push({
            value: value,
            label: question.human[index] || question.data[index]
          });
        });
      }
    }

    group[question.key] = new FormGroup({
      value: new FormControl(autoCompleteValue || ''),
      input: new FormControl({ value: '', disabled: question.settings && question.settings.disabled === true })
    });
  }

  setPaymentControls(question: any, group: any) {
    const questionGroup: any = {};

    if (question?.settings?.payment_options && question.settings.payment_options.length) {
      question.settings.payment_options.forEach((option: any) => {
        if (option.service === 'stripe') {
          questionGroup.stripe = this.setStripeControls();
        }
        if (option.service === 'gocardless') {
          questionGroup.gocardless = this.setGocardlessControls();
        }
      });

      let defaultPaymentOption = question.settings.payment_options[0]?.service;

      if (question.const_is_staff) { 
        defaultPaymentOption = '';
      }
      questionGroup.selected_payment_option = new FormControl(defaultPaymentOption);
    }
    group[question.key] = new FormGroup(questionGroup);
  }

  /**
   *
   * @param question
   * @param group
   * @description Set controls for stripe form component
   */
  setStripeControls() {
    return new FormGroup({});
  }

  /**
   *
   * @param question
   * @param group
   * @description Set controls for stripe form component
   */
  setGocardlessControls() {
    return new FormGroup({
      ddname: new FormControl(''),
      ddbsb: new FormControl(''),
      ddaccnum: new FormControl(''),
      account_created: new FormControl('')
    });
  }

  /**
   *
   * @param question
   * @param group
   * @description Set controls for special question layouts
   */
  setLayoutControls(question: any, group: any) {
    const layoutSettings = question.layout;

    if (['date_input_3_boxes', 'date_select_month_year'].indexOf(layoutSettings) > -1) {
      const value = this.raptorDataService.convertToDateObject(question.data);
      let initialDayValue = value ? value.day : '';
      if (layoutSettings === 'date_select_month_year') {
        initialDayValue = initialDayValue || '01';
      }

      group[question.key] = new FormGroup({
        day: new FormControl({ value: initialDayValue, disabled: (question.settings && question.settings.disabled === true) }),
        month: new FormControl({ value: value ? value.month : '', disabled: (question.settings && question.settings.disabled === true) }),
        year: new FormControl({ value: value ? value.year : '', disabled: (question.settings && question.settings.disabled === true) })
      });
    }

    if (layoutSettings === 'range') {
      const value = this.raptorDataService.convertToDateObject(question.data);
      
      group[question.key] = new FormGroup({
        start: new FormControl({
          value: question.data && question.data.start !== null && question.data.start !==  undefined ? question.data.start : '',
          disabled: (question.settings && question.settings.disabled === true) 
        }),
        end: new FormControl({
          value: question.data && question.data.end !== null && question.data.end !==  undefined ? question.data.end : '',
          disabled: (question.settings && question.settings.disabled === true) 
        })
      });
    }

    if (layoutSettings === 'bupa_mem_num') {
      let suffix = '';
      let number = '';
      if (question.data) {
        suffix = question.data.split('-').pop() || '';
      }
      if (question.data) {
        number = question.data.split('-')[0];
      }
      group[question.key] = new FormGroup({
        number: new FormControl({ value: number || '', disabled: (question.settings && question.settings.disabled === true) }),
        suffix: new FormControl({ value: suffix, disabled: (question.settings && question.settings.disabled === true) }),
        type: new FormControl({ value: question.bupaMemberType, disabled: (question.settings && question.settings.disabled === true) })
      });
    }
  }

  /**
   *
   * @param question
   * @param group
   * @description Set controls for normal question
   */
  setQuestionControl(question: any, group: any) {
    group[question.key] = new FormControl({
      value: question.data !== null && question.data !==  undefined ? question.data : '',
      disabled: (question.settings && question.settings.disabled === true)
    });
  }

  /**
   *
   * @param question
   * @param group
   * @description Set controls for mobile question
   */
  setMobileControls(question: any, group: any) {
    let mobileNumberValues = [];
    let defaultCountryCode = "";
    
    if (question.data) {
      mobileNumberValues = question.data.split(' ');
    }

    if (question.settings?.default_country_code) {
      defaultCountryCode = RAPTOR_COUNTRY_CODES[question.settings.default_country_code as keyof typeof RAPTOR_COUNTRY_CODES]?.code;
    } else if (question.settings?.country_alpha3.length > 0) {
      defaultCountryCode = RAPTOR_COUNTRY_CODES[question.settings.country_alpha3[0] as keyof typeof RAPTOR_COUNTRY_CODES]?.code;
    }
    
    group[question.key] = new FormGroup({
      country: new FormControl({ 
        value: mobileNumberValues?.[0] || defaultCountryCode, 
        disabled: (question.settings && question.settings.disabled === true) 
      }),
      mobileNumber: new FormControl({ 
        value: mobileNumberValues?.[1] || '', 
        disabled: (question.settings && question.settings.disabled === true) 
      })
    });
  }

  /**
   *
   * @param previous
   * @param current
   * @returns string - question key
   * @description Returns the key of the question that has been changed. Used on the valueChanges handler
   */
  public formValueChanged(previous: any, current: any) {
    let changedField!: string;

    for (const value in current) {
      if (typeof current[value] === 'object') {
        if (valueNotEqual(previous[value], current[value], value)) {
          changedField = value;
        }
      } else if (previous[value] !== current[value]) {
        changedField = value;
      }
      if (changedField) {
        break;
      }
    }

    function valueNotEqual(previous: any, current: any, value: any) {
      let field!: string;
      
      if (current && typeof current === 'object') {
        if (Array.isArray(current)) {
          if (Array.isArray(previous) && current.length === previous.length) {
            current.every((element, index) => {
              if (current[index] && previous[index]) {
                field = valueNotEqual(current[index], previous[index], index);
              }
              // break the loop if the value has changed
              return !field;
            });
          } else {
            field = value;
          }
        } else if (current._isAMomentObject) {
          // for the date pickers just check that the _i values match
          if ((previous && previous._i) !== (current && current._i)) {
            field = value;
          }
        } else {
          for (const key in current) {
            if (current && previous) {
              field = valueNotEqual(current[key], previous[key], key);
              // break the loop if the value has changed
              if (field) {
                break;
              }
            }
          }
        }
      } else if (previous !== current) {
        field = value;
      }
      return field;
    }

    return changedField;
  }

  /**
   *
   * @param formData
   * @param form
   * @description Helper function that applies value formatting on input change event
   */
  public applyQuestionFormatting(formData: any, form: any) {
    this.raptorDataService.getAllFormInputs(formData).forEach((question: any) => {
      const control = form.controls[question.key];
      const { value } = control;
      const patchedValues: any = {};

      if (question.type === 'currency' && value) {
        patchedValues[question.key] = this.raptorCurrencyPipe.transform(question, value);
      }

      if (question.type === 'number' && value) {
        if (question.settings?.format === 'currency') {
          patchedValues[question.key] = this.raptorCurrencyPipe.transform(question, value);
        } else {
          patchedValues[question.key] = this.raptorDecimalPipe.transform(question, value);
        }
      }

      form.patchValue(patchedValues, { emitEvent: false });
    });
  }

  /**
   *
   * @param formData
   * @param form
   * @param formValues
   * @description Find all the questions that have form controllers and create validators for them
   */
  setValidators(formData: any, form: any, formValues: any) {
    if (formData.sections && formData.sections.length) {
      formData.sections.forEach((section: any) => {
        if (section.elements && section.elements.length) {
          section.elements.forEach((question: any) => {
            if (question.layout !== 'review' && question.key) {
              this.setQuestionValidators(form, formValues, question, section);
            }
          });
        }
      });
    }

    if (formData.sidebar && formData.sidebar.sections && formData.sidebar.sections.length) {
      formData.sidebar.sections.forEach((section: any) => {
        if (section.elements && section.elements.length) {
          section.elements.forEach((question: any) => {
            if (question.layout !== 'review' && question.key) {
              this.setQuestionValidators(form, formValues, question, section);
            }
          });
        }
      });
    }
  }

  /**
   *
   * @param formData
   * @param formValues
   * @description Find all the questions that have predicates and check their validation
   * If any of these predicates fail then the update action should not be allowed to fire
   * as this may cause a flow rendering error on the server
   */
  checkFormPredicates(formData: any, formValues: any): boolean {
    let invalidPredicates = false;

    if (formData.sections && formData.sections.length) {
      formData.sections.forEach((section: any) => {
        if (section.elements && section.elements.length) {
          section.elements.forEach((question: any) => {
            if (question.layout !== 'review' && question.key) {
              const questionInvalid = this.checkPredicates(formValues, question, section);
              if (questionInvalid) {
                invalidPredicates = true;
              }
            }
          });
        }
      });
    }

    if (formData.sidebar && formData.sidebar.sections && formData.sidebar.sections.length) {
      formData.sidebar.sections.forEach((section: any) => {
        if (section.elements && section.elements.length) {
          section.elements.forEach((question: any) => {
            if (question.layout !== 'review' && question.key) {
              const questionInvalid = this.checkPredicates(formValues, question, section);
              if (questionInvalid) {
                invalidPredicates = true;
              }
            }
          });
        }
      });
    }

    return invalidPredicates;
  }

  /**
   *
   * @param formData
   * @param question
   * @param section
   * @description Checks the validity of the predicates of a question based upon whether
   * the question is visible or not.
   */
  checkPredicates(formValues: any, question: any, section: any): boolean {
    let showSection;
    let showQuestion;
    let invalidPredicates = false;

    if (section.show_if && typeof section.show_if === 'object') {
      showSection = this.predicateService.validator(section.show_if, formValues);
    }

    if (question.show_if && typeof question.show_if === 'object') {
      showQuestion = this.predicateService.validator(question.show_if, formValues);
    }

    if (
      showSection &&
      showQuestion &&
      question.validations &&
      question.validations.length
    ) {
      question.validations.forEach((validator: any) => {
        const isValid = this.predicateService.validator(validator.predicate, formValues);
        if (!isValid) {
          invalidPredicates = true;
        }
      });
    }

    return invalidPredicates;
  }

  /**
   *
   * @param question
   * @param section
   * @param form
   * @param formValues
   * @description Adds repeater field validators when saving a repeater item
   */
  setRepeaterValidators(question: any, section: any, form: any, formValues: any) {
    const fields = question.elements.flat();
    fields.forEach((repeaterQuestion: any) => {
      // check if repeater question is visible before checking it's validators
      // otherwise it will reset the control which may be shared by another repeater
      // question that has the same key
      const sectionControl = form.get(question.key);
      let showQuestion = true;
      if (repeaterQuestion.show_if && typeof repeaterQuestion.show_if === 'object') {
        showQuestion = this.predicateService.validator(repeaterQuestion.show_if, formValues);
      }
      if (showQuestion) {
        this.setQuestionValidators(form, formValues, repeaterQuestion, section);
      }
    });
  }

  /**
   *
   * @param question
   * @param formValues
   * @description Removes repeater field validators when editing a repeater item
   */
  removeRepeaterValidators(question: any, form: any) {
    question.elements.forEach((repeaterQuestion: any) => {
      const sectionControl = form.get(question.key);
      const questionControl = form.get(repeaterQuestion.key);
      questionControl.clearValidators();
      this.resetHiddenControl(questionControl);
    });
  }

  /**
   *
   * @param questionControls - paymet question form controller
   * @param type - payment type stripe/gocardless
   * @secription Sets the validation for a payment form
   */
  setPaymentValidators(questionControls: any, type: string) {
    const paymentFields = ['ddname', 'ddbsb', 'ddaccnum'];
    const setValidators = ((controls: any) => {
      paymentFields.forEach((field: string) => {
        if (controls[field]) {
          controls[field].setValidators([Validators.required]);
          controls[field].updateValueAndValidity();
        }
      });
    });

    this.removePaymentValidators(questionControls);

    if (questionControls.controls) {
      if (type === 'gocardless') {
        const gocardlessControls = questionControls.controls.gocardless.controls;
        setValidators(gocardlessControls);
      }

      if (type === 'stripe') {
        const stripeControls = questionControls.controls.stripe.controls;
        setValidators(stripeControls);
      }
    }
  }

  /**
   *
   * @param questionControls - paymet question form controller
   * @description Removes all validation on gocardlkess and stripe payment forms
   */
  removePaymentValidators(questionControls: any) {
    const paymentFields = ['ddname', 'ddbsb', 'ddaccnum'];
    const unsetValidators = ((controls: any) => {
      paymentFields.forEach((field: string) => {
        if (controls && controls[field]) {
          controls[field].clearValidators();
        }
      });
    });

    if (questionControls.controls) {
      if (questionControls.controls.gocardless) {
        unsetValidators(questionControls.controls.gocardless.controls);
      }

      if (questionControls.controls.stripe) {
        unsetValidators(questionControls.controls.stripe.controls);
      }
    }
  }

  /**
   *
   * @param form
   * @param formValues
   * @param question
   * @param section
   * @returns Form control object for the question
   * @description Test that the question is visible. If it is then attach any validators
   */
  setQuestionValidators(form: any, formValues: any, question: any, section: any) {
    const service = this;
    const formControls = form.controls;
    const control = formControls[question.key];
    const validatorArray: Array<any> = [];

    control.clearValidators();
    // check if the field is visible before applying any validation
    // if not then return before adding any validation
    if (section && section.show_if && typeof section.show_if === 'object') {
      // test section predicate
      const showSection = this.predicateService.validator(section.show_if, formValues);
      if (!showSection) {
        this.resetHiddenControl(control);
        return;
      }
    }

    if (question.show_if && typeof question.show_if === 'object') {
      // test question predicate
      const showQuestion = this.predicateService.validator(question.show_if, formValues);
      if (!showQuestion) {
        this.resetHiddenControl(control);
        return;
      }
    }

    if (question.hasOwnProperty('required') && (question.required === true || question.required === 'true')) {
      validatorArray.push(Validators.required);
      // set up a custom required validator for multiple choice inputs
      if (question.type === 'multiple_choice') {
        const requiredValidator = function customRequiredValidator(control: any) {
          return service.checkboxRequiredValidator(control, question, formValues);
        };
        validatorArray.push(requiredValidator);
      } else if (control.controls) {
        // generic group i.e. 3 date box
        const validator = function customValidator(control: any) {
          return service.groupRequiredValidator(control);
        };
        validatorArray.push(validator);
      }
    }

    if (
      question.type === 'string' &&
      question.layout &&
      question.layout === 'email_input'
    ) {
      validatorArray.push(Validators.email);
      if (this.environment.production === false) {
        const validator = function customValidator() {
          return RaptorControlService.companyEmailValidator(control);
        };
        validatorArray.push(validator);
      }
    }

    if (question.type === 'date') {
      const currentDate = new Date();

      if (question.settings && typeof question.settings.min_date === 'number') {
        const minDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        minDate.setDate(minDate.getDate() + question.settings.min_date);
        validatorArray.push(this.minDateValidator(minDate, question));
      }

      if (question.settings && typeof question.settings.max_date === 'number') {
        const maxDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        maxDate.setDate(maxDate.getDate() + question.settings.max_date);
        validatorArray.push(this.maxDateValidator(maxDate, question));
      }
    }

    if (question.type === 'repeater' && question.layout === 'multiple') {
      question.fields.forEach((fieldGroup: any) => {
        fieldGroup.forEach((column: any) => {
          column.forEach((field: any) => {
            let showQuestion = true;
            if (field.show_if && typeof field.show_if === 'object') {
              showQuestion = this.predicateService.validator(field.show_if, formValues);
            }
            if (showQuestion && field.key) {
              this.setQuestionValidators(form, formValues, field, section);
            }
          });
        })
      });
    }

    if (question.type === 'mobile') {
      validatorArray.push(this.mobileNumberValidator(control));
    }

    if (control.controls) {
      // set multi-field question controls
      this.setGroupValidators(control.controls, question.required);
    }

    if (question.validations && question.validations.length) {
      question.validations.forEach((validation: any, index: number) => {
        // might have to test if there is a predicate on the validator
        const validator = function customValidator(control: any) {
          return service.predicateValidator(validation, index, control, question, formValues);
        };
        validatorArray.push(validator);
      });
    }

    validatorArray.length ? control.setValidators(validatorArray) : control.setValidators(null);
    control.updateValueAndValidity();
  }

  /**
   *
   * @param validation
   * @param index
   * @param control
   * @param question
   * @param formValues
   * @returns Custome validation function
   * @description Creates a function which is attached to the control's validator array
   */
  predicateValidator(validation: any, index: number, control: any, question: any, formValues: any) {
    const validatorId = `validator_${index.toString()}`;
    const invalidObject: any = {};
    let invalid;
    let fieldValue = control.value;

    invalidObject[validatorId] = { message: validation.message, layout: validation.layout };

    // for complex questions the question form group needs updating
    if (control.controls) {
      fieldValue = this.getFormGroupValue(control, question);
    }

    // predicate function
    if (fieldValue !== null) {
      invalid = !this.predicateService.validator(validation.predicate, formValues);
    }

    return invalid ? invalidObject : null;
  }

  /**
   *
   * @param control
   * @returns error object or null
   * @description Custom required validation function for checkbox groups
   */
  groupRequiredValidator(control: any) {
    const validatorId = 'validator_required';
    const invalidObject: any = {};
    let invalid = true;

    invalidObject[validatorId] = { message: 'This question is required' };

    // for complex questions the question form group needs updating
    // all controls need to not have their own validation message showing
    if (control.controls) {
      for (const field in control.controls) {
        if (
          !control.controls[field].errors ||
          !control.controls[field].errors.required === true ||
          !control.controls[field].pristine
        ) {
          invalid = false;
        }
      }
    }

    return invalid ? invalidObject : null;
  }

  /**
   *
   * @param control
   * @param question
   * @param formValues
   * @returns Boolean - validation result
   * @description Custom required validation function for checkbox groups
   */
  checkboxRequiredValidator(control: any, question: any, formValues: any) {
    let hasValue;
    const invalidObject = {
      multipleChoice_required: { message: 'This question is required' }
    };

    if (formValues[question.key]) {
      hasValue = false;
      for (const key in formValues[question.key]) {
        if (formValues[question.key][key] === true) {
          hasValue = true;
        }
      }
    } else {
      // this needs to be set to invalid in order for the form to be in an error state
      hasValue = false;
    }
    return hasValue ? null : invalidObject;
  }

  /**
   *
   * @param groupControls
   * @param isRequired
   * @description Custom group validation function
   */
  setGroupValidators(groupControls: any, isRequired: boolean) {
    // day-month-year controls
    if (groupControls.day && groupControls.month && groupControls.year) {
      const dayValidatorArray: Array<any> = [];
      const monthValidatorArray: Array<any> = [];
      const yearValidatorArray: Array<any> = [];

      groupControls.day.clearValidators();
      groupControls.month.clearValidators();
      groupControls.year.clearValidators();

      if (isRequired) {
        dayValidatorArray.push(Validators.required);
        monthValidatorArray.push(Validators.required);
        yearValidatorArray.push(Validators.required);
      }

      // default month and year input validations
      monthValidatorArray.push(Validators.max(12));
      yearValidatorArray.push(Validators.min(1000));
      yearValidatorArray.push(Validators.max(9999));

      if (groupControls.month.valid && groupControls.year.valid) {
        const day30 = [4, 6, 9, 11];
        const day31 = [1, 3, 5, 7, 8, 10, 12];

        if (day30.indexOf(groupControls.month.value) > -1) {
          dayValidatorArray.push(Validators.max(30));
        }
        if (day31.indexOf(groupControls.month.value) > -1) {
          dayValidatorArray.push(Validators.max(31));
        }
        if (groupControls.month.value === 2) {
          let leapYear = false;
          if (groupControls.year.value % 400 === 0) {
            leapYear = true;
          } else if (groupControls.year.value % 100 === 0) {
            leapYear = false;
          } else if (groupControls.year.value % 4 === 0) {
            leapYear = true;
          }
          dayValidatorArray.push(Validators.max(leapYear ? 29 : 28));
        }
      } else {
        dayValidatorArray.push(Validators.max(31));
      }

      dayValidatorArray.length ? groupControls.day.setValidators(dayValidatorArray) : groupControls.day.setValidators(null);
      groupControls.day.updateValueAndValidity();

      monthValidatorArray.length ? groupControls.month.setValidators(monthValidatorArray) : groupControls.month.setValidators(null);
      groupControls.month.updateValueAndValidity();

      yearValidatorArray.length ? groupControls.year.setValidators(yearValidatorArray) : groupControls.year.setValidators(null);
      groupControls.year.updateValueAndValidity();
    }
    // date range picker controls
    if (groupControls.start && groupControls.end) {
      groupControls.start.clearValidators();
      groupControls.end.clearValidators();

      groupControls.start.setValidators([Validators.required]);
      groupControls.end.setValidators([Validators.required]);

      groupControls.start.updateValueAndValidity();
      groupControls.end.updateValueAndValidity();
    }
    // autocomplete-multiple controls
    if (
      groupControls.input &&
      isRequired &&
      !(Array.isArray(groupControls.value.value) && groupControls.value.value.length)
    ) {
      groupControls.input.clearValidators();
      groupControls.input.setValidators([Validators.required]);
      groupControls.input.updateValueAndValidity();
    }
    // set validator controls
    if (groupControls.number && groupControls.suffix && isRequired) {
      for (const key in groupControls) {
        if (key !== 'type') {
          groupControls[key].clearValidators();
        
          if (key === 'number'){
            groupControls[key].setValidators([Validators.required]);
          }
  
          if (key === 'suffix'){
            groupControls[key].setValidators(Validators.maxLength(2));
            groupControls[key].setValidators([Validators.required]);
          }
  
          groupControls[key].updateValueAndValidity();
        }
      }
    }
  }

  static companyEmailValidator(control: any): any {
    let invalid;
    const validatorId = 'staff_email';
    const validatorMessage = `
      Test COIs are company property, and must only be sent to company email addresses, 
      hence the restriction on email addresses to the beopen.com, bupa.com.au  and ahm.com.au 
      domains. You are advised that forwarding COIs to a personal email address is a violation of company policy.
    `;
    const allowedEmails = ['beopen.com', 'huddle.com.au', 'bupa.com.au', 'ahm.com.au', 'synthetics.dtdg.co'];
    const suffix = '+';
    const invalidObject: any = {};
    const fieldValue = control.value;
    const domain = fieldValue.split('@').pop();

    invalidObject[validatorId] = { message: validatorMessage };

    if (fieldValue) {
      const emailDomain = allowedEmails.indexOf(domain);
      const hasCorrectDomain = emailDomain > -1;
      const suffixIndex = fieldValue.indexOf(suffix);
      const AtIndex = fieldValue.indexOf('@');
      const suffixRequired = emailDomain > -1 && emailDomain < 2;
      const suffixValid = suffixRequired && suffixIndex >= 0 && suffixIndex < AtIndex - 1;
      invalid = !hasCorrectDomain || (suffixRequired && !suffixValid);
    }

    return invalid ? invalidObject : null;
  }

  /**
   * Custom validator for mobile numbers
   * Checks if the number is a valid mobile number format based on country code
   */
  private mobileNumberValidator(control: any) {
    return () => {
      const mobileNumber = control.get('mobileNumber').value;
      const country = control.get('country')?.value;
      
      if (!mobileNumber) {
        return null;
      }

      const normalizedNumber = mobileNumber.replace(/^0/, '');
      const formattedPhoneNumber = this.raptorPhonePipe.transform(country, normalizedNumber);
      const countryObject = Object.values(RAPTOR_COUNTRY_CODES).find(countryItem => countryItem.code === country);
      const mobileRegex = countryObject?.validation || RAPTOR_COUNTRY_CODES.AUS.validation;
      
      return mobileRegex.test(formattedPhoneNumber) ? null :{
        invalidMobileFormat: {
          message: `Please enter a valid mobile number`
        }
      }
    }
  }

  /**
   *
   * @param field
   * @param question
   * @returns String - value of a group of input controls i.e. A date object
   */
  getFormGroupValue(field: any, question: any): any {
    let validationValue: any;
    let valid = true;

    for (const control in field.controls) {
      if (field.controls[control].invalid) {
        valid = false;
      }
    }

    if (valid) {
      if (question.type === 'date') {
        if (question.layout === 'date_input_3_boxes' || question.layout === 'date_select_month_year') {
          validationValue = this.getDateValue(field.value);
        }
        if (question.layout === 'range') {
          validationValue = field.value.start;
        }
      }

      if (question.type === 'autocomplete') {
        validationValue = field.value;
      }

      if (question.type === 'autocomplete_multiple') {
        validationValue = field.value.value;
      }
    }

    return validationValue;
  }

  /**
   *
   * @param value
   * @returns Date - date object
   */
  getDateValue(value: any) {
    const dateObj = [value.year, value.month, value.day].join('-');
    return dateObj;
  }

  /**
   *
   * @param control
   * @description - Resets the controls of a question that is now hidden in the form
   */
  resetHiddenControl(control: any) {
    control.patchValue(null, { emitEvent: false });
    control.reset();
    if (control.controls) {
      // clearing group validators
      for (const key in control.controls) {
        control.controls[key].clearValidators();
        control.controls[key].patchValue(null, { emitEvent: false });
        control.controls[key].reset();
      }
    }
  }

  public minDateValidator(minDate: any, question: any): ValidatorFn {
    return (control: any): any => {
      if (control.value) {
        const answerValue = this.predicateService.convertToDate(control.value);
        if (answerValue) {
          const answerDate = new Date(answerValue.getFullYear(), answerValue.getMonth(), answerValue.getDate());
          return answerDate.valueOf() < minDate.valueOf() ?
            {
              validator_mindate: {
                message: question.settings && question.settings.min_error_message || 'Please choose a date from within 30 days from today'
              }
            } :
            null;
        }
      }
      return null;
    };
  }

  public maxDateValidator(maxDate: any, question: any): ValidatorFn {
    return (control: any): any => {
      if (control.value) {
        const answerValue = this.predicateService.convertToDate(control.value);
        if (answerValue) {
          const answerDate = new Date(answerValue.getFullYear(), answerValue.getMonth(), answerValue.getDate());
          return answerDate.valueOf() > maxDate.valueOf() ?
            {
              validator_maxdate: {
                message: question.settings && question.settings.max_error_message || 'Please choose a date from within 30 days from today'
              }
            } :
            null;
        }
      }
      return null;
    };
  }
}
